import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import Yup from "yup";
import get from "lodash/get";
import has from "lodash/has";
import pick from "lodash/pick";
import {
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableFooter,
  TextField,
  Button,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { withStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import { Delete as DeleteIcon } from "@mui/icons-material";
import Panel from "common/components/Panel";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import actions from "../actions";
import Pagination from "common/components/Pagination";
import Resource from "common/components/Resource";
import resourceFactory from "common/admin/resourceFactory";
import QRCodeModal from "common/components/QRCodeModal";
import BuildingForm from "../Form";
import { transformApiError } from "common/api/errors";
import ResourceAdmins from "core/ResourceAdmins";
import fields from "../fields";
import { FORM_SUBMISSION_ERROR } from "common/constants";
import DownloadCSV from "core/DownloadCSV";
import { UnitsModal } from "./UnitsModal";

const handleSubmit = (id) => async (values, form) => {
  const body = {
    ...pick(values, fields),
    hours: JSON.stringify(values.hours),
  };

  const { setSubmitting, setErrors, setStatus } = form;

  try {
    if (values.heroFile) {
      const heroUrls = await actions.getURLForFiles(values.heroFile);
      body.hero_url = heroUrls.toString();
    }
    await actions.update(id, body);

    await actions.attachFiles(id, [
      {
        prop: "logo_url",
        name: "logo",
        file: values.logoFile,
      },
    ]);

    setStatus({ success: true });
  } catch (error) {
    const apiError = transformApiError(error);

    if (apiError.empty()) {
      setErrors({ http: error.message });
    } else {
      setErrors(apiError.object());
    }

    setSubmitting(false);
  }
};

const EditBuilding = ({ classes, match }) => {
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCompanyName, setEditedCompanyName] = useState("");
  const [editedCompanyOrder, setEditedCompanyOrder] = useState("");

  const handleEditClick = (company) => {
    setIsEditing(true);
    setSelectedCompany(company);
    setEditedCompanyName(company.name);
    setEditedCompanyOrder(company.company_order);
  };

  const handleNameChange = (event) => {
    setEditedCompanyName(event.target.value);
  };

  const handleOrderChange = (event) => {
    setEditedCompanyOrder(event.target.value);
  };

  const handleSaveClick = async (reload) => {
    try {
      await resourceFactory("companies").update(selectedCompany.id, {
        name: editedCompanyName,
        company_order: editedCompanyOrder,
      });
      setIsEditing(false);
      reload(); // Reload the data after saving
    } catch (error) {
      console.error("Error updating company:", error);
    }
  };

  return (
    <Fragment>
      <Resource
        name="buildings"
        resourceId={match.params.id}
        render={(building) => (
          <Panel
            title="Edit Building"
            action={<QRCodeModal content={building.redemption_url} />}
            secondaryAction={
              <DownloadCSV
                apiUrl={`buildings/${match.params.id}/download_redemptions`}
                title="Redemptions"
              />
            }
          >
            <BuildingForm
              edit
              handleSubmit={handleSubmit(building.id)}
              initialValues={{
                name: building.name || "",
                street_address: building.street_address || "",
                city: building.city || "",
                region: building.region || "",
                country: building.country || "",
                postal_code: building.postal_code || "",
                community_id: building.community_id || 0,
                active: building.active || false,
                sign_up_inactive: building.sign_up_inactive || false,
                heroUrl: building.hero_url || "",
                otherHeroUrl: building.other_hero_urls || "",
                logoUrl: building.logo_url || "",
                primary_phone_number: building.primary_phone_number || "",
                website_url: building.website_url || "",
                description: building.description || "",
                hours: building.hours || {},
                complex_name: building.complex_name || "",
                is_hilo: building.is_hilo,
                tenant_id: building.tenant_id || 0,
              }}
            />
          </Panel>
        )}
      />
      <Panel title="Floors">
        <Resource
          name="floors"
          queryParams={{ building_id: match.params.id }}
          render={(floors, { reload, removeOne, prevPage, nextPage, page }) => (
            <>
              <UnitsModal
                selectedFloor={selectedFloor}
                setSelectedFloor={setSelectedFloor}
                reload={reload}
              />
              <Table className={classes.table}>
                <TableBody>
                  {floors.length ? (
                    floors.map((floor) => (
                      <TableRow hover key={floor.id}>
                        <TableCell width="25%">Level {floor.level}</TableCell>
                        <TableCell>
                          {!floor.units || floor.units.length === 0
                            ? "No units"
                            : floor.units.map(({ name }) => (
                                <Chip
                                  key={name}
                                  label={name}
                                  style={{ marginRight: 5, marginBottom: 5 }}
                                />
                              ))}
                        </TableCell>
                        <TableCell align="right" width="30%">
                          <Button
                            sx={{ mr: 2 }}
                            variant="contained"
                            onClick={() => setSelectedFloor(floor)}
                          >
                            Add / Edit Units
                          </Button>
                          <IconButton
                            onClick={() =>
                              removeOne(floor.id, {
                                building_id: match.params.id,
                              })
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>No levels found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={6} className={classes.pagination}>
                      <Pagination
                        page={page}
                        nextPage={nextPage}
                        prevPage={prevPage}
                        items={floors}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} className={classes.pagination}>
                      <Resource
                        name="floors"
                        queryParams={{ building_id: match.params.id }}
                        render={() => (
                          <Formik
                            enableReinitialize
                            initialValues={{
                              buildingId: match.params.id,
                              level: "",
                            }}
                            validationSchema={Yup.object().shape({
                              buildingId: Yup.string().required(
                                "Building id is required",
                              ),
                              level: Yup.string().required("Level is required"),
                            })}
                            onSubmit={async (values, form) => {
                              const { buildingId, level } = values;
                              const floorsActions = resourceFactory("floors");
                              const {
                                setSubmitting,
                                setErrors,
                                setStatus,
                                resetForm,
                              } = form;
                              try {
                                await floorsActions.create({
                                  building_id: buildingId,
                                  level,
                                });
                                resetForm();
                                setStatus({ success: true });

                                /// Refresh floors list
                                reload();
                              } catch (e) {
                                setErrors({ http: e.message });
                              }
                              setSubmitting(false);
                            }}
                          >
                            {({
                              values,
                              errors,
                              touched,
                              handleChange,
                              isSubmitting,
                              status,
                              setValues,
                            }) => (
                              <Form>
                                <Grid container spacing={3}>
                                  <Grid size={{ xs: 12, sm: 2 }}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      error={
                                        touched.level && has(errors, "level")
                                      }
                                      id="level"
                                      type="level"
                                      size="small"
                                      label="New level (e.g. 1)"
                                      helperText={
                                        touched.level && get(errors, "level")
                                      }
                                      onChange={handleChange}
                                      value={values.level}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 2 }}>
                                    <Button
                                      style={{
                                        marginTop: 2,
                                        display: "inline-block",
                                      }}
                                      type="submit"
                                      variant="contained"
                                      color="primary"
                                    >
                                      Add floor
                                    </Button>
                                    {has(errors, "http") && (
                                      <SimpleSnackbar
                                        message={FORM_SUBMISSION_ERROR}
                                      />
                                    )}
                                  </Grid>
                                </Grid>
                                <Grid size={{ xs: 12 }} />
                                {get(status, "success") && (
                                  <SimpleSnackbar message="New floor succesfully added!" />
                                )}
                              </Form>
                            )}
                          </Formik>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </>
          )}
        />
      </Panel>
      <Panel title="Companies">
        <Resource
          name="companies"
          queryParams={{ building_id: match.params.id }}
          render={(companies, { reload, prevPage, nextPage, page }) => (
            <>
              <UnitsModal
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                reload={reload}
              />
              <Table className={classes.table}>
                <TableBody>
                  {companies.length ? (
                    companies.map((company) => (
                      <TableRow hover key={company.id}>
                        <TableCell width="75%">
                          {isEditing &&
                          selectedCompany &&
                          selectedCompany.id === company.id ? (
                            <TextField
                              value={editedCompanyName}
                              fullWidth
                              onChange={handleNameChange}
                            />
                          ) : (
                            company.name
                          )}
                        </TableCell>
                        <TableCell>
                          {isEditing &&
                          selectedCompany &&
                          selectedCompany.id === company.id ? (
                            <TextField
                              value={editedCompanyOrder}
                              fullWidth
                              onChange={handleOrderChange}
                            />
                          ) : (
                            <Chip
                              key={company.company_order}
                              label={company.company_order}
                              style={{ marginRight: 5, marginBottom: 5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" width="30%">
                          {isEditing &&
                          selectedCompany &&
                          selectedCompany.id === company.id ? (
                            <>
                              <Button
                                sx={{ mr: 2 }}
                                variant="contained"
                                onClick={() => handleSaveClick(reload)}
                              >
                                Save
                              </Button>
                              <Button
                                sx={{ ml: 2 }}
                                variant="contained"
                                onClick={() => setIsEditing(false)}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              sx={{ mr: 2 }}
                              variant="contained"
                              onClick={() => handleEditClick(company)}
                            >
                              Edit Company Name
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>No Companies found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={6} className={classes.pagination}>
                      <Pagination
                        page={page}
                        nextPage={nextPage}
                        prevPage={prevPage}
                        items={companies}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} className={classes.pagination}>
                      <Resource
                        name="companies"
                        queryParams={{ building_id: match.params.id }}
                        render={() => (
                          <Formik
                            enableReinitialize
                            initialValues={{
                              buildingId: match.params.id,
                              name: "",
                            }}
                            validationSchema={Yup.object().shape({
                              buildingId: Yup.string().required(
                                "Building id is required",
                              ),
                              name: Yup.string().required("Name is required"),
                            })}
                            onSubmit={async (values, form) => {
                              const { buildingId, name } = values;
                              const companyActions =
                                resourceFactory("companies");
                              const {
                                setSubmitting,
                                setErrors,
                                setStatus,
                                resetForm,
                              } = form;
                              try {
                                await companyActions.create({
                                  building_id: buildingId,
                                  name: name,
                                });
                                resetForm();
                                setStatus({ success: true });

                                /// Refresh floors list
                                reload();
                              } catch (e) {
                                setErrors({ http: e.message });
                              }
                              setSubmitting(false);
                            }}
                            render={({ values, errors, touched, handleChange, status }) => (
                              <Form>
                                <Grid container spacing={3}>
                                  <Grid size={{ xs: 12, sm: 2 }}>
                                    <TextField
                                      fullWidth
                                      variant="outlined"
                                      error={
                                        touched.name && has(errors, "name")
                                      }
                                      id="name"
                                      type="name"
                                      size="small"
                                      label="New company"
                                      helperText={
                                        touched.name && get(errors, "name")
                                      }
                                      onChange={handleChange}
                                      value={values.name}
                                      slotProps={{
                                        htmlInput: { maxLength: 255 },
                                      }}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, sm: 2 }}>
                                    <Button
                                      style={{
                                        marginTop: 2,
                                        display: "inline-block",
                                      }}
                                      type="submit"
                                      variant="contained"
                                      color="primary"
                                    >
                                      Add Company
                                    </Button>
                                    {has(errors, "http") && (
                                      <SimpleSnackbar
                                        message={FORM_SUBMISSION_ERROR}
                                      />
                                    )}
                                  </Grid>
                                </Grid>
                                <Grid size={{ xs: 12 }} />
                                {get(status, "success") && (
                                  <SimpleSnackbar message="New company succesfully added!" />
                                )}
                              </Form>
                            )}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </>
          )}
        />
      </Panel>
      <ResourceAdmins
        resourceType="building"
        resourceId={parseInt(match.params.id, 10)}
      />
    </Fragment>
  );
};

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 180,
  },
});

EditBuilding.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object,
};

export default withStyles(styles)(EditBuilding);
